import type { IPayoutBank } from '../types/global/payout.types';

/** Paystack secret-key test mode only allows a small set of resolve codes (esp. 001). */
export const PAYSTACK_TEST_BANK_CODES = ['001'] as const;

/**
 * Countries that still resolve bank accounts through Paystack.
 * Only Nigeria — GH/KE/ZA bank + all MoMo use Flutterwave.
 */
const PAYSTACK_BANK_COUNTRIES = new Set(['NG']);

/** sessionStorage key for admin toggle (non-prod only). */
export const PAYSTACK_TEST_BANK_MODE_KEY = 'thspian.paystackTestBankMode';

/**
 * Live bank resolves only in production by default.
 * qa / stage / uat / dev / development use Paystack test banks to avoid
 * "Test mode daily limit of 3 live bank resolves exceeded".
 */
export function isLivePayoutBankEnvironment(): boolean {
    const env = (process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV || '').toLowerCase();
    return env === 'production';
}

export function isPaystackBankCountry(country?: string): boolean {
    if (!country) return false;
    return PAYSTACK_BANK_COUNTRIES.has(country.toUpperCase());
}

/** Paystack test-bank UI/filter applies only to NG bank/NUBAN — never MoMo. */
export function isPaystackTestBankMethod(country?: string, methodId?: string): boolean {
    if (!isPaystackBankCountry(country)) return false;
    return methodId === 'nuban' || methodId === 'bank_account';
}

/** Admins/super-admins can toggle test ↔ live banks outside production. */
export function canTogglePaystackTestBankMode(isAdminOrSuperAdmin: boolean): boolean {
    return !isLivePayoutBankEnvironment() && isAdminOrSuperAdmin;
}

export function readPaystackTestBankModePreference(defaultValue = true): boolean {
    if (typeof window === 'undefined' || isLivePayoutBankEnvironment()) return defaultValue;
    try {
        const raw = window.sessionStorage.getItem(PAYSTACK_TEST_BANK_MODE_KEY);
        if (raw === null) return defaultValue;
        return raw === '1' || raw === 'true';
    } catch {
        return defaultValue;
    }
}

export function writePaystackTestBankModePreference(enabled: boolean): void {
    if (typeof window === 'undefined' || isLivePayoutBankEnvironment()) return;
    try {
        window.sessionStorage.setItem(PAYSTACK_TEST_BANK_MODE_KEY, enabled ? '1' : '0');
    } catch {
        // ignore
    }
}

/**
 * Restrict to test banks when:
 * - NG bank/NUBAN only (not Flutterwave MoMo/bank),
 * - not production, and
 * - test mode is on (default), OR caller is not an admin who opted into live banks
 */
export function shouldRestrictToPaystackTestBanks(
    country?: string,
    opts?: { testModeEnabled?: boolean; methodId?: string },
): boolean {
    if (isLivePayoutBankEnvironment()) return false;
    if (opts?.testModeEnabled === false) return false;
    if (!isPaystackTestBankMethod(country, opts?.methodId ?? 'bank_account')) return false;
    return true;
}

export function isPaystackTestBankCode(code?: string | null): boolean {
    if (!code) return false;
    const normalized = String(code).trim();
    return (PAYSTACK_TEST_BANK_CODES as readonly string[]).includes(normalized);
}

const FALLBACK_TEST_BANK: IPayoutBank = {
    id: 'paystack-test-001',
    name: 'Test Bank (Paystack)',
    code: '001',
};

/** Keep only Paystack test banks when restricted; always ensure 001 is available. */
export function filterBanksForPayoutEnvironment<T extends { code?: string; name?: string }>(
    banks: T[],
    country?: string,
    opts?: { testModeEnabled?: boolean; methodId?: string },
): T[] {
    if (!shouldRestrictToPaystackTestBanks(country, opts)) return banks;

    const filtered = banks.filter(b => isPaystackTestBankCode(b.code));
    if (filtered.length > 0) return filtered;

    return [FALLBACK_TEST_BANK as unknown as T];
}

export function paystackTestBankHint(): string {
    return 'Paystack test mode: use bank code 001 only. Live bank resolves are limited outside production.';
}

export function flutterwaveMomoHint(country?: string): string {
    const cc = (country || '').toUpperCase();
    if (cc === 'KE') {
        return 'Kenya mobile money pays out via Flutterwave (M-Pesa MPS or Airtel MPX). Enter the phone number registered on the wallet.';
    }
    if (cc === 'GH') {
        return 'Ghana mobile money pays out via Flutterwave. Choose MTN, Vodafone, or AirtelTigo and enter the wallet phone number.';
    }
    return 'Mobile money pays out via Flutterwave. Choose your provider and enter the registered phone number.';
}
